import React from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, BorderRadius } from '../constants/theme';

interface SuccessionReadinessMeterProps {
  readiness: number; // 0-100
  targetRole?: string;
}

const Container = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.lg}px;
`;

const MeterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${Spacing.lg}px;
  position: relative;
`;

const CircularProgress = styled.div<{ readiness: number; color: string }>`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: conic-gradient(
    ${props => props.color} 0deg,
    ${props => props.color} ${props => (props.readiness / 100) * 360}deg,
    ${Colors.border} ${props => (props.readiness / 100) * 360}deg,
    ${Colors.border} 360deg
  );
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 15px;
    left: 15px;
    right: 15px;
    bottom: 15px;
    background-color: ${Colors.surface};
    border-radius: 50%;
  }
`;

const CenterText = styled.div`
  position: relative;
  text-align: center;
  z-index: 1;
`;

const ReadinessValue = styled.div`
  font-size: 36px;
  font-weight: bold;
  color: ${Colors.textPrimary};
  line-height: 1;
`;

const ReadinessLabel = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-top: ${Spacing.xs}px;
`;

const InfoContainer = styled.div`
  text-align: center;
`;

const StatusBadge = styled.span<{ color: string }>`
  background-color: ${props => props.color};
  color: ${Colors.textPrimary};
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  margin-bottom: ${Spacing.md}px;
  display: inline-block;
`;

const TargetRole = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.lg}px;
`;

const FactorsContainer = styled.div`
  width: 100%;
`;

const FactorsTitle = styled.h4`
  font-size: ${FontSizes.sm}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.md}px;
`;

const FactorRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.sm}px;
`;

const FactorLabel = styled.span`
  flex: 1;
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const FactorBar = styled.div`
  flex: 2;
  height: 6px;
  background-color: ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  margin: 0 ${Spacing.sm}px;
  overflow: hidden;
`;

const FactorProgress = styled.div<{ width: string; color: string }>`
  height: 100%;
  width: ${props => props.width};
  background-color: ${props => props.color};
  border-radius: ${BorderRadius.sm}px;
  transition: width 0.3s ease;
`;

const FactorValue = styled.span`
  min-width: 35px;
  text-align: right;
  font-size: ${FontSizes.xs}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
`;

const SuccessionReadinessMeter: React.FC<SuccessionReadinessMeterProps> = ({
  readiness,
  targetRole = 'Next Level',
}) => {
  const getReadinessLevel = () => {
    if (readiness >= 90) return { label: 'Ready Now', color: Colors.success };
    if (readiness >= 70) return { label: 'Ready in 6-12 months', color: Colors.warning };
    if (readiness >= 50) return { label: 'Ready in 1-2 years', color: Colors.info };
    return { label: 'Development Needed', color: Colors.danger };
  };

  const readinessLevel = getReadinessLevel();

  const factors = [
    { label: 'Performance', value: 92, color: Colors.success },
    { label: 'Skills', value: 85, color: Colors.success },
    { label: 'Experience', value: 70, color: Colors.warning },
    { label: 'Leadership', value: 65, color: Colors.warning },
  ];

  return (
    <Container>
      <MeterContainer>
        <CircularProgress readiness={readiness} color={readinessLevel.color}>
          <CenterText>
            <ReadinessValue>{readiness}%</ReadinessValue>
            <ReadinessLabel>Readiness</ReadinessLabel>
          </CenterText>
        </CircularProgress>
      </MeterContainer>

      <InfoContainer>
        <StatusBadge color={readinessLevel.color}>
          {readinessLevel.label}
        </StatusBadge>
        
        <TargetRole>Target Role: {targetRole}</TargetRole>
        
        <FactorsContainer>
          <FactorsTitle>Readiness Factors:</FactorsTitle>
          
          {factors.map((factor, index) => (
            <FactorRow key={index}>
              <FactorLabel>{factor.label}</FactorLabel>
              <FactorBar>
                <FactorProgress 
                  width={`${factor.value}%`} 
                  color={factor.color} 
                />
              </FactorBar>
              <FactorValue>{factor.value}%</FactorValue>
            </FactorRow>
          ))}
        </FactorsContainer>
      </InfoContainer>
    </Container>
  );
};

export default SuccessionReadinessMeter;